import React from 'react';
import { Box, Typography, Button, ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useThemeMode } from '../contexts/ThemeContext';

const NotFoundPage = () => {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  
  const theme = createTheme({
    palette: {
      mode,
      ...(mode === 'dark' ? {
        background: {
          default: '#141420',
          paper: '#1A1A25'
        },
        text: {
          primary: '#E0E0E5',
          secondary: '#A0A0B0'
        }
      } : {
        background: {
          default: '#FAFAFA',
          paper: '#FFFFFF'
        },
        text: {
          primary: '#000000',
          secondary: '#666666'
        }
      })
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme => theme.palette.mode === 'dark' 
          ? 'radial-gradient(circle at 50% 50%, #141420 0%, #0A0A0F 100%)'
          : 'radial-gradient(circle at 50% 50%, #FAFAFA 0%, #F0F0F0 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: theme => theme.palette.mode === 'dark'
            ? `radial-gradient(circle at center, rgba(138, 43, 226, 0.1) 0%, transparent 50%),
               repeating-linear-gradient(45deg, rgba(138, 43, 226, 0.05) 0px, rgba(138, 43, 226, 0.05) 2px, transparent 2px, transparent 10px),
               repeating-linear-gradient(-45deg, rgba(138, 43, 226, 0.05) 0px, rgba(138, 43, 226, 0.05) 2px, transparent 2px, transparent 10px)`
            : `radial-gradient(circle at center, rgba(138, 43, 226, 0.05) 0%, transparent 50%),
               repeating-linear-gradient(45deg, rgba(138, 43, 226, 0.02) 0px, rgba(138, 43, 226, 0.02) 2px, transparent 2px, transparent 10px),
               repeating-linear-gradient(-45deg, rgba(138, 43, 226, 0.02) 0px, rgba(138, 43, 226, 0.02) 2px, transparent 2px, transparent 10px)`,
          backgroundSize: '100% 100%, 20px 20px, 20px 20px',
          animation: 'gradientMove 20s ease-in-out infinite',
          zIndex: 0,
        },
        '@keyframes gradientMove': {
          '0%, 100%': { 
            backgroundPosition: '0% 0%, 0px 0px, 0px 0px',
            opacity: 0.8
          },
          '50%': { 
            backgroundPosition: '0% 0%, 10px 10px, -10px -10px',
            opacity: 1
          }
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          px: 3
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '4rem', sm: '6rem' },
            fontWeight: 700,
            background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
            textShadow: theme => theme.palette.mode === 'dark'
              ? '0 0 30px rgba(138, 43, 226, 0.3)'
              : '0 0 30px rgba(138, 43, 226, 0.2)'
          }}
        >
          404
        </Typography>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            color: theme => theme.palette.mode === 'dark' ? '#E0E0E5' : '#000000',
            fontWeight: 500
          }}
        >
          Space not found
        </Typography>
        <Button
          variant="contained"
          startIcon={<RocketLaunchIcon />}
          onClick={() => navigate('/')}
          sx={{
            backgroundColor: '#8A2BE2',
            color: '#FFFFFF',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1.1rem',
            fontWeight: 500,
            boxShadow: '0 4px 14px rgba(138, 43, 226, 0.25)',
            '&:hover': {
              backgroundColor: '#7B1FA2',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(138, 43, 226, 0.3)',
            },
            transition: 'all 0.3s ease'
          }}
        >
          Back to Earth
        </Button>
      </Box>
    </Box>
    </ThemeProvider>
  );
};

export default NotFoundPage;
